import React, { useEffect, useMemo, useState } from 'react';
import BlogPostImage from './BlogPostImage';
import { normalizePost } from '../utils/wordpress';
import '../styles/blogComponent.css';

const WP_API_BASE = process.env.REACT_APP_WP_API_BASE || 'https://public-api.wordpress.com/wp/v2/sites/qashqgmail.wordpress.com';
const SITE_BASE = process.env.REACT_APP_SITE_BASE || 'https://qashqgmail.wordpress.com/';

function getSlidesToShow(width) {
  if (width < 768) return 1;
  if (width < 992) return 2;
  return 3;
}

function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(() =>
    typeof window !== 'undefined' ? getSlidesToShow(window.innerWidth) : 3,
  );

  useEffect(() => {
    const handleResize = () => {
      const next = getSlidesToShow(window.innerWidth);
      setSlidesToShow(next);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    async function loadPosts() {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${WP_API_BASE}/posts?per_page=6&_embed`);
        if (!response.ok) {
          throw new Error('Unable to fetch blog posts right now.');
        }
        const data = await response.json();
        setPosts(data || []);
        setCurrentIndex(0);
      } catch (err) {
        setError(err.message || 'Something went wrong while loading posts.');
      } finally {
        setLoading(false);
      }
    }

    loadPosts();
  }, []);

  const normalizedPosts = useMemo(
    () => posts.map((post) => normalizePost(post, SITE_BASE)),
    [posts],
  );

  const itemsToDisplay = Math.min(normalizedPosts.length, slidesToShow);
  const maxIndex = Math.max(0, normalizedPosts.length - slidesToShow);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const totalSlides = Math.max(1, Math.ceil(normalizedPosts.length / slidesToShow));

  return (
    <section className="blog-component" id="blog">
      <div className="container">
        <h2 className="blog-component-heading">Latest Insights</h2>

        {error && <p className="blog-error-message">{error}</p>}

        {loading ? (
          <div className="blog-loading">
            <p>Loading blog posts...</p>
          </div>
        ) : normalizedPosts.length > 0 ? (
          <>
            <div className="blog-slider-wrapper">
              <button
                className="blog-slider-btn prev-btn"
                onClick={handlePrev}
                aria-label="Previous posts"
                style={{ visibility: normalizedPosts.length <= slidesToShow ? 'hidden' : 'visible' }}
              >
                <ion-icon name="chevron-back-outline"></ion-icon>
              </button>

              <div className="blog-slider-container">
                <div
                  className="blog-slider-track"
                  style={{
                    display: 'flex',
                    gap: slidesToShow === 1 ? '0px' : '20px',
                    transform: `translateX(-${currentIndex * (100 / slidesToShow)}%)`,
                    transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    width: `${Math.max(100, (normalizedPosts.length / slidesToShow) * 100)}%`,
                    justifyContent: itemsToDisplay < slidesToShow ? 'center' : 'flex-start',
                  }}
                >
                  {normalizedPosts.map((post) => (
                    <article
                      key={post.id}
                      className="blog-component-card"
                      style={{
                        flex: slidesToShow === 1 ? '0 0 100%' : `0 0 calc(${100 / slidesToShow}% - ${(slidesToShow - 1) * 20 / slidesToShow}px)`,
                        minWidth: slidesToShow === 1 ? '100%' : `calc(${100 / slidesToShow}% - ${(slidesToShow - 1) * 20 / slidesToShow}px)`,
                        maxWidth: slidesToShow === 1 ? '100%' : 'none',
                      }}
                    >
                      <figure className="blog-component-banner">
                        <BlogPostImage src={post.image} alt={post.title} />
                      </figure>

                      <div className="blog-component-content">
                        <p className="blog-component-date">
                          <ion-icon name="calendar-outline"></ion-icon>
                          <time dateTime={post.date}>{post.date}</time>
                        </p>

                        <h3 className="blog-component-title">
                          <a href={post.link} target="_blank" rel="noopener noreferrer">
                            {post.title}
                          </a>
                        </h3>

                        <p className="blog-component-excerpt">{post.excerpt}</p>

                        <a className="blog-component-read-more" href={post.link} target="_blank" rel="noopener noreferrer">
                          Read More
                          <ion-icon name="arrow-forward-outline"></ion-icon>
                        </a>
                      </div>
                    </article>
                  ))}
                </div>
              </div>

              <button
                className="blog-slider-btn next-btn"
                onClick={handleNext}
                aria-label="Next posts"
                style={{ visibility: normalizedPosts.length <= slidesToShow ? 'hidden' : 'visible' }}
              >
                <ion-icon name="chevron-forward-outline"></ion-icon>
              </button>
            </div>

            {normalizedPosts.length > slidesToShow && (
              <div className="blog-component-dots">
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${currentIndex === index ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  ></button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="blog-empty">
            <p>No blog posts available at the moment.</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default Blog;
